import { useEffect, useState } from 'react'
import { usePublicClient } from 'wagmi'
import { ADDRESSES, IS_LIVE, ZERO_ADDR } from '@/lib/contracts'
import safetyModuleAbi from '@/lib/abis/SafetyModule.json'
import { getLogsChunked } from '@/lib/getLogsChunked'

function ev(abi, name) { return abi.find((x) => x.type === 'event' && x.name === name) }
const ABSORBED = ev(safetyModuleAbi, 'Absorbed')


/// Real, trailing "additional APY" a Guardian Pool has actually realized from liquidations, on top
/// of the base supply interest every depositor already earns. Reconstructed from real `Absorbed`
/// events, the same client-side event-log approach useHistoricalStats already uses:
/// `recovered - repaid - bounty` per event is exactly the net profit (or loss) left inside the
/// module raising (or lowering) totalAssets for stakers, once the keeper's own bounty cut is
/// excluded. Annualized over the module's real lifetime since the pool deployed - not since the
/// first liquidation - so a module that's sat idle for months shows a correspondingly small
/// realized rate rather than one based on a cherry-picked active window.
///
/// Parameterized by MODULE (there is one Guardian Pool per reserve, entry 165) and by the defended
/// asset's DECIMALS - the event amounts are denominated in that asset, and dividing by 1e18
/// unconditionally was exactly the entry-85 bug class waiting for the USDC (6) and WBTC (8) pools.
/// `netProfit` is returned in the defended asset's own units; the caller multiplies by its price
/// for display.
///
/// No fabricated or projected number: zero liquidations so far is a real, honest 0%, and a net
/// loss shows as a real negative figure. Returns bps (can be negative), or `null` while loading.
export function useSafetyModuleExtraApy(moduleAddress, decimals, totalAssets) {
  const client = usePublicClient()
  const [extraAprBps, setExtraAprBps] = useState(null)
  // The cumulative figure was already being computed and thrown away. Surfacing it costs nothing
  // and answers a question the rate cannot: an APY says how fast, a total says how much.
  const [netProfit, setNetProfit] = useState(null)
  // How many absorptions the figures above are built from. Zero means the pool has no liquidation
  // story yet - the UI hides the scoreboard rather than opening with "+0.00%", which reads as
  // "this pays nothing" on a page whose whole pitch is the opposite. Never fabricated either way:
  // the first real absorption makes both figures appear with real values.
  const [absorptionCount, setAbsorptionCount] = useState(0)

  useEffect(() => {
    const notDeployed = !IS_LIVE || !moduleAddress || moduleAddress.toLowerCase() === ZERO_ADDR
    if (notDeployed || !client) { setExtraAprBps(0); setNetProfit(null); setAbsorptionCount(0); return }
    let cancelled = false

    async function run() {
      try {
        const fromBlock = ADDRESSES.LENDING_POOL_DEPLOY_BLOCK ? BigInt(ADDRESSES.LENDING_POOL_DEPLOY_BLOCK) : 0n
        const [logs, fromBlockInfo, latestBlockInfo] = await Promise.all([
          // Windowed, same reason as useHistoricalStats - a real RPC rejects a full-history range.
          getLogsChunked(client, { address: moduleAddress, event: ABSORBED, fromBlock }),
          client.getBlock({ blockNumber: fromBlock }),
          client.getBlock({ blockTag: 'latest' }),
        ])
        if (cancelled) return

        // `recovered`, per the asset-generic rename (entry 165). The previous name
        // (`usdxRecovered`) would decode as undefined against the current ABI and silently zero
        // this whole figure - names in the ABI decide what viem hands back, even for old logs.
        setAbsorptionCount(logs.length)
        let netProfitWei = 0n
        for (const l of logs) netProfitWei += l.args.recovered - l.args.repaid - l.args.bounty

        const elapsedSeconds = Number(latestBlockInfo.timestamp - fromBlockInfo.timestamp)
        const profit = Number(netProfitWei) / 10 ** (decimals ?? 18)
        setNetProfit(profit)
        if (elapsedSeconds <= 0 || !(totalAssets > 0)) { setExtraAprBps(0); return }

        const annualizedRate = (profit / totalAssets) * ((365 * 24 * 60 * 60) / elapsedSeconds)
        setExtraAprBps(annualizedRate * 10_000)
      } catch (e) {
        console.error('useSafetyModuleExtraApy: getLogs scan failed', e)
        if (!cancelled) { setExtraAprBps(0); setNetProfit(null) }
      }
    }
    run()
    return () => { cancelled = true }
  }, [client, moduleAddress, decimals, totalAssets])

  return { extraAprBps, netProfit, absorptionCount }
}
